import i18next from "i18next";
import { EnumLanguage } from "../generated/enums";
import { Language, LANGUAGES } from "./languages";

export const LANGUAGE_STORAGE_KEY = "language";

// Les navigateurs annoncent le chinois en « zh-CN », « zh-TW »… alors que nos locales
// l'appellent « cn ».
const browserPrefixToLanguage: Record<string, Language> = {
  fr: "fr",
  en: "en",
  es: "es",
  zh: "cn",
};

export const accountLanguageToLanguage: Record<EnumLanguage, Language> = {
  fr_FR: "fr",
  en_GB: "en",
  es_ES: "es",
  zh_CN: "cn",
};

export const languageToAccountLanguage: Record<Language, EnumLanguage> = {
  fr: "fr_FR",
  en: "en_GB",
  es: "es_ES",
  cn: "zh_CN",
};

const isLanguage = (value: string): value is Language =>
  (LANGUAGES as readonly string[]).includes(value);

/**
 * Le dernier choix (globe ou profil) l'emporte ; sinon la première langue du navigateur
 * qu'on traduit ; sinon l'anglais, plus lisible que le français pour un étranger.
 */
export const detectLanguage = (
  storedLanguage: string,
  browserLanguages: readonly string[]
): Language => {
  if (isLanguage(storedLanguage)) return storedLanguage;
  const supported = browserLanguages
    .map((tag) => browserPrefixToLanguage[tag.toLowerCase().split("-")[0]])
    .find(Boolean);
  return supported ?? "en";
};

export const readStoredLanguage = (): string => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
};

/** Affiche le site dans cette langue, et la retient pour les visites suivantes. */
export const applyLanguage = (language: Language) => {
  i18next.changeLanguage(language);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Stockage bloqué (navigation privée) : le choix vaut pour la session seulement.
  }
};

/** Langue enregistrée sur un compte créé maintenant : celle qu'il voit à l'écran. */
export const currentAccountLanguage = (): EnumLanguage =>
  languageToAccountLanguage[
    isLanguage(i18next.language) ? i18next.language : "en"
  ];
